import { useState, type FormEvent } from 'react';
import { Send } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { notificationsApi } from '@/features/institute/notifications/api/notifications.api';
import { useInstituteBranding } from '@/features/institute/store/branding.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { ApiRequestError } from '@/lib/apiClient';
import { formatCurrency, formatDate } from '@/lib/format';
import type { FeeAccountRow, FeeReminderResult } from '@/types/fees';
import type { MessageChannel } from '@/types/messaging';
import { feesApi } from '../api/fees.api';
import {
  DEFAULT_REMINDER_MESSAGE,
  DUE_WHEN_CHOICES,
  REMINDER_DUE_PRESETS,
  dueWhenFilter,
  type DueWhenChoice,
} from '../constants';
import { DueWhenFilter } from './DueWhenFilter';
import { ReminderMessageField, smsParts } from './ReminderMessageField';

/**
 * Every channel this screen knows how to compose for.
 *
 * Used only when the server reports that none are configured, so the screen can
 * still be walked through before an administrator adds the sender credentials.
 * Nothing goes out in that state — the server refuses a send it has no sender
 * for — and the screen says so rather than implying one.
 */
const ALL_CHANNELS = [
  { value: 'SMS', label: 'SMS' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
];

/**
 * One family's figures, as the server fills them into a reminder — carried so
 * the preview shows the message that family will actually read.
 */
export interface ReminderFamily {
  fullName: string;
  balance: string;
  admissionNumber: string;
  /** The next unpaid installment's name; null without a schedule. */
  installment: string | null;
  /** `YYYY-MM-DD` — the account's own date, else the next installment's. */
  dueDate: string | null;
}

/** Who this send is chasing, as the dialog was opened for. */
export type ReminderTarget =
  | { kind: 'ALL_DUE' }
  | ({
      kind: 'STUDENT';
      studentUuid: string;
      /** The session the register is open on; null until it has loaded. */
      session: string | null;
    } & ReminderFamily);

/** A register row's figures, as a reminder fills them in. */
export function reminderFamilyOf(account: FeeAccountRow): ReminderFamily {
  return {
    fullName: account.fullName,
    balance: account.balance,
    admissionNumber: account.admissionNumber,
    installment: account.nextInstallment?.name ?? null,
    dueDate: account.dueDate,
  };
}

export interface FeeReminderModalProps {
  target: ReminderTarget;
  onClose: () => void;
  /** The register redraws afterwards — nothing about a balance changed, but a
   *  send is worth reflecting if a colleague collected in the meantime. */
  onSent: () => void;
}

/**
 * Reminding the families who have not paid.
 *
 * The office writes one message and every parent reads their own figures: the
 * amount, the child's name and the admission number are placeholders the server
 * fills in per family. A reminder that said only "fees are due" would have two
 * hundred parents ringing the office to ask how much.
 *
 * The confirmation step is deliberate, exactly as it is on the broadcast
 * screen. Every other Save in this product is reversible by editing the record
 * again; this one arrives on a parent's handset, costs the institute money, and
 * cannot be recalled.
 *
 * Who receives it is never in this screen's gift: it names "everybody who owes"
 * or one student's public id, and the server resolves the parent's number —
 * father, mother or whoever the family named — from its own roll.
 */
export function FeeReminderModal({ target, onClose, onSent }: FeeReminderModalProps) {
  const isSingle = target.kind === 'STUDENT';
  const instituteName = useInstituteBranding((state) => state.branding?.displayName ?? null);

  const [channel, setChannel] = useState<MessageChannel>('SMS');
  const [message, setMessage] = useState(DEFAULT_REMINDER_MESSAGE);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<FeeReminderResult | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  /** "Owes at least / at most", as typed — digits only, blank for no limit. */
  const [minDue, setMinDue] = useState('');
  const [maxDue, setMaxDue] = useState('');
  /** When the fees fall due — overdue, or due within a few days. */
  const [dueChoice, setDueChoice] = useState<DueWhenChoice>('ANY');
  const dueWhen = dueWhenFilter(dueChoice);
  const isDated = dueWhen.dueWhen !== undefined;

  const band = toBand(minDue, maxDue);
  const settledBand = toBand(useDebouncedValue(minDue), useDebouncedValue(maxDue));
  const isBanded = band.minBalance !== undefined || band.maxBalance !== undefined;
  // Mirrors the server's `refineDueRange`: a band nobody can fall in is a mistake.
  const bandError =
    band.minBalance !== undefined && band.maxBalance !== undefined && band.minBalance > band.maxBalance
      ? 'Must be at least the amount on the left'
      : undefined;

  /**
   * How many families this would reach, and how much it is chasing.
   *
   * Read from the register itself rather than from a count of its own: the
   * statuses on that screen and the students reminded here have to be the same
   * set, and a second definition of "owes something" is one that can disagree.
   * One row is asked for: the summary, and the family the preview is drawn
   * from — the largest balance, as the register sorts by default.
   */
  const settledBandError =
    settledBand.minBalance !== undefined &&
    settledBand.maxBalance !== undefined &&
    settledBand.minBalance > settledBand.maxBalance;
  const { data: register, isLoading: isCountLoading } = useApiQuery(
    () =>
      isSingle || settledBandError
        ? Promise.resolve(null)
        : feesApi.listAccounts({ page: 1, limit: 1, ...settledBand, ...dueWhen }),
    [isSingle, settledBand.minBalance, settledBand.maxBalance, settledBandError, dueChoice],
  );
  /** The count on screen is not yet for the band as typed — still settling, or on its way. */
  const isCounting =
    !isSingle &&
    (isCountLoading ||
      band.minBalance !== settledBand.minBalance ||
      band.maxBalance !== settledBand.maxBalance);

  /** Whether this installation can send at all. The server decides either way. */
  const { data: audience, isLoading: isAudienceLoading } = useApiQuery(
    () => notificationsApi.audience(),
    [],
  );

  const configuredChannels = [
    ...(audience?.channels.sms ? [{ value: 'SMS', label: 'SMS' }] : []),
    ...(audience?.channels.whatsapp ? [{ value: 'WHATSAPP', label: 'WhatsApp' }] : []),
  ];
  const isConfigured = configuredChannels.length > 0;
  const channelOptions = isConfigured ? configuredChannels : ALL_CHANNELS;

  /**
   * The channel actually in force — derived, because the state is initialised
   * before the audience arrives. An installation with WhatsApp but no SMS
   * sender would otherwise show "WhatsApp" while the state still said SMS.
   */
  const available = channelOptions.map((option) => option.value);
  const activeChannel = (
    available.includes(channel) ? channel : (available[0] ?? 'SMS')
  ) as MessageChannel;

  const dueCount = isSingle
    ? 1
    : (register?.byStatus.UNPAID ?? 0) + (register?.byStatus.PARTIAL ?? 0);
  const outstanding = isSingle ? target.balance : (register?.totals.balance ?? '0');

  /** Whose message the preview shows — the one student, or the first who owes. */
  const firstOwing = register?.items[0];
  const previewFamily = isSingle ? target : firstOwing ? reminderFamilyOf(firstOwing) : null;
  const previewSession = isSingle ? target.session : (register?.academicYear.name ?? null);

  // The first answer only: once the form is up, a new band must not swap it
  // for a skeleton and take the cursor out of the amount being typed.
  const isLoading =
    (isCountLoading && !register && !isSingle && !isBanded && !isDated) || isAudienceLoading;
  const parts = smsParts(message);

  function review(event: FormEvent) {
    event.preventDefault();
    setIsConfirming(true);
  }

  async function send() {
    setIsSending(true);
    setSendError(null);

    try {
      const sent = await feesApi.sendReminders({
        channel: activeChannel,
        message: message.trim(),
        scope: isSingle ? 'SELECTED_STUDENTS' : 'ALL_DUE',
        // Sent only for one student — the server refuses a list alongside
        // "everybody who owes", rather than quietly widening the send.
        ...(isSingle && { studentUuids: [target.studentUuid] }),
        ...(!isSingle && band),
        ...(!isSingle && dueWhen),
      });
      setResult(sent);
      setIsConfirming(false);
      onSent();
    } catch (caught) {
      // The server names the reason — no credentials, nobody outstanding, a
      // student no longer on the roll — so it is shown verbatim, and inside the
      // dialog rather than as a toast that would sit behind it.
      setSendError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
      setIsConfirming(false);
    } finally {
      setIsSending(false);
    }
  }

  // --- what happened, once it has gone out ---------------------------------

  if (result) {
    return (
      <Modal
        isOpen
        onClose={onClose}
        title="Reminders sent"
        size="sm"
        footer={
          <Button size="sm" onClick={onClose}>
            Done
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="success">{result.sent} sent</Badge>
            {result.failed > 0 && <Badge tone="critical">{result.failed} failed</Badge>}
            {result.unreachable.length > 0 && (
              <Badge tone="warning">{result.unreachable.length} no number</Badge>
            )}
          </div>

          <p className="text-sm text-ink-2">
            Chasing{' '}
            <strong className="font-mono font-[560] text-ink">
              {formatCurrency(result.totalOutstanding)}
            </strong>{' '}
            in all.
          </p>

          {result.failures.length > 0 && (
            <div className="rounded-card border border-line">
              <p className="lbl border-b border-line bg-surface-2 px-3 py-2">
                Could not be reached
              </p>
              <ul className="divide-y divide-line">
                {result.failures.map((failure) => (
                  <li key={`${failure.name}-${failure.mobile}`} className="px-3 py-2">
                    <p className="text-sm text-ink">{failure.name}</p>
                    <p className="font-mono text-xs tabular-nums text-ink-3">{failure.mobile}</p>
                    <p className="text-xs text-crit">{failure.reason}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.unreachable.length > 0 && (
            <Alert tone="warning" title={`${result.unreachable.length} have no mobile number`}>
              <NameList names={result.unreachable.map((student) => student.name)} />. Add a parent's
              number to their record so the family hears about the next one.
            </Alert>
          )}

          {/* Named by the office but not written to. Reported rather than
              dropped: an office that ticked somebody expects to hear why. */}
          {result.settled.length > 0 && (
            <Alert tone="info" title={`${result.settled.length} had already paid`}>
              <NameList names={result.settled.map((student) => student.name)} />. Nothing was sent
              to them.
            </Alert>
          )}

          {result.withoutFees.length > 0 && (
            <Alert tone="info" title={`${result.withoutFees.length} have no fees set`}>
              <NameList names={result.withoutFees.map((student) => student.name)} />. There is no
              amount to ask for until somebody sets what they are charged.
            </Alert>
          )}
        </div>
      </Modal>
    );
  }

  // --- confirming ----------------------------------------------------------

  if (isConfirming) {
    return (
      <Modal
        isOpen
        onClose={() => setIsConfirming(false)}
        size="sm"
        title={
          isSingle
            ? `Remind ${target.fullName}'s family?`
            : `Remind the families of ${countStudents(dueCount)}?`
        }
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsConfirming(false)}
              disabled={isSending}
            >
              Back
            </Button>
            <Button
              size="sm"
              isLoading={isSending}
              leftIcon={<Send className="h-4 w-4" />}
              onClick={() => void send()}
            >
              {isSending ? 'Sending…' : `Send ${dueCount === 1 ? 'reminder' : `${dueCount} reminders`}`}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {isConfigured ? (
            <Alert tone="warning" title="This cannot be undone">
              The {dueCount === 1 ? 'family' : 'families'} of {countStudents(dueCount)} will
              receive this by{' '}
              {activeChannel === 'SMS' ? 'SMS' : 'WhatsApp'}
              {activeChannel === 'SMS' && parts > 1
                ? `, billed as ${parts} messages each`
                : ''}
              , chasing {formatCurrency(outstanding)} in all
              {isBanded && ` — only students owing ${describeBand(band)}`}
              {isDated && ` — only fees ${describeDue(dueChoice)}`}. There is no way to recall it.
            </Alert>
          ) : (
            <Alert tone="warning" title="Nothing will be delivered">
              No sender is configured on this installation, so this send will be refused by the
              server. Once an administrator adds the credentials it would reach the{' '}
              {dueCount === 1 ? 'family' : 'families'} of {countStudents(dueCount)}.
            </Alert>
          )}

          <div className="rounded-card border border-line bg-surface-2 p-3">
            <p className="lbl">
              {previewFamily
                ? `What ${previewFamily.fullName}'s family will read`
                : 'What one family will read'}
            </p>
            {/* The placeholders shown filled in, not raw: an office reviewing
                `{amount}` is reviewing the template, not the message. */}
            <p className="mt-1 whitespace-pre-wrap text-sm text-ink">
              {preview(message, previewFamily, previewSession, instituteName)}
            </p>
            {!isSingle && previewFamily && dueCount > 1 && (
              <p className="mt-1 text-xs text-ink-3">
                Each family reads their own child's figures.
              </p>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  // --- composing -----------------------------------------------------------

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={isSingle ? `Remind ${target.fullName}'s family` : 'Send fee reminders'}
      description={
        isSingle
          ? `${formatCurrency(target.balance)} outstanding. Goes to the parent's mobile on their record.`
          : "Goes to every family with something still outstanding this session, on the parent's mobile. Narrow it by when the fees fall due or how much they owe."
      }
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          {(dueCount > 0 || isBanded || isDated) && (
            <Button
              size="sm"
              form="fee-reminder-form"
              type="submit"
              disabled={message.trim().length === 0 || dueCount === 0 || isCounting || Boolean(bandError)}
              leftIcon={<Send className="h-4 w-4" />}
            >
              Review and send{dueCount > 1 ? ` to ${dueCount}` : ''}
            </Button>
          )}
        </>
      }
    >
      {isLoading ? (
        <div className="flex flex-col gap-3" role="status" aria-label="Working out who owes">
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
      ) : (
        <form id="fee-reminder-form" onSubmit={review} className="flex flex-col gap-4">
          {sendError && (
            <Alert tone="critical" title="Could not send these reminders">
              {sendError}
            </Alert>
          )}

          {!isConfigured && (
            <Alert tone="warning" title="Notifications are not set up yet">
              This installation has no SMS or WhatsApp sender configured, so nothing will actually
              be delivered. An administrator adds the notification service credentials on the
              server; once they have, this screen works without any further change.
            </Alert>
          )}

          {!isSingle && (
            <fieldset className="flex flex-col gap-2">
              <legend className="lbl mb-1">When due</legend>
              <DueWhenFilter value={dueChoice} onChange={setDueChoice} />
              <p className="text-xs text-ink-3">
                By each family's own due date, or else their next unpaid installment's. Families
                with no due date set are left out of these.
              </p>
            </fieldset>
          )}

          {!isSingle && (
            <fieldset className="flex flex-col gap-2">
              <legend className="lbl mb-1">Who owes</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  label="Owes at least (₹)"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="No minimum"
                  maxLength={9}
                  value={minDue}
                  onChange={(event) => setMinDue(toRupeeDigits(event.target.value))}
                />
                <Input
                  label="Owes at most (₹)"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="No maximum"
                  maxLength={9}
                  value={maxDue}
                  error={bandError}
                  onChange={(event) => setMaxDue(toRupeeDigits(event.target.value))}
                />
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {REMINDER_DUE_PRESETS.map((amount) => {
                  const isActive = band.minBalance === amount && band.maxBalance === undefined;
                  return (
                    <button
                      key={amount}
                      type="button"
                      aria-pressed={isActive}
                      onClick={() => {
                        setMinDue(String(amount));
                        setMaxDue('');
                      }}
                      className={
                        isActive
                          ? 'pill bg-accent-soft text-[12px] text-accent'
                          : 'pill bg-surface-2 text-[12px] text-ink-2 transition-colors hover:bg-accent-soft hover:text-accent'
                      }
                    >
                      {formatCurrency(String(amount))}+
                    </button>
                  );
                })}
                {isBanded && (
                  <button
                    type="button"
                    onClick={() => {
                      setMinDue('');
                      setMaxDue('');
                    }}
                    className="pill bg-surface-2 text-[12px] text-ink-2 transition-colors hover:bg-accent-soft hover:text-accent"
                  >
                    Any amount
                  </button>
                )}
              </div>
            </fieldset>
          )}

          {dueCount === 0 && (isBanded || isDated) ? (
            isCounting ? null : (
              <Alert tone="info" title="Nobody matches these choices">
                {isDated
                  ? `No family has fees ${describeDue(dueChoice)}${isBanded ? ' in that range' : ''}. Choose another option to include more.`
                  : 'Widen the range to include more families.'}
              </Alert>
            )
          ) : dueCount === 0 ? (
            <Alert tone="success" title="Nobody owes anything">
              Every account on the register is settled for this session, so there is no one to
              remind.
            </Alert>
          ) : (
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,11rem)] sm:items-end">
              <div className="rounded-card border border-line bg-surface-2 px-3 py-2.5">
                <p className="lbl">Going to</p>
                <p className="mt-0.5 text-sm text-ink">
                  {isSingle ? (
                    <>
                      {target.fullName}'s parent —{' '}
                      <strong className="font-mono font-[560]">
                        {formatCurrency(outstanding)}
                      </strong>{' '}
                      outstanding
                    </>
                  ) : (
                    <span aria-live="polite" className={isCounting ? 'text-ink-3' : undefined}>
                      {countStudents(dueCount)} —{' '}
                      <strong className="font-mono font-[560]">
                        {formatCurrency(outstanding)}
                      </strong>{' '}
                      outstanding in all
                      {isCounting && ' · counting…'}
                    </span>
                  )}
                </p>
              </div>

              <Select
                label="Send by"
                options={channelOptions}
                disabled={channelOptions.length < 2}
                value={activeChannel}
                onChange={(event) => setChannel(event.target.value as MessageChannel)}
              />
            </div>
          )}

          <ReminderMessageField message={message} onChange={setMessage} channel={activeChannel} />
        </form>
      )}
    </Modal>
  );
}

/**
 * The template as one real family would read it.
 *
 * Filled the way the server fills it — the same fallbacks for a family with no
 * schedule — so the preview is the message, not an illustration of one. A
 * value this screen does not hold yet shows as a dash, never a made-up figure,
 * and an unknown token is left as typed: the server refuses those anyway.
 */
function preview(
  template: string,
  family: ReminderFamily | null,
  session: string | null,
  institute: string | null,
): string {
  const values: Record<string, string> = {
    name: family?.fullName ?? '—',
    amount: family ? formatCurrency(family.balance) : '—',
    admissionNumber: family?.admissionNumber ?? '—',
    installment: family ? (family.installment ?? 'Fees') : '—',
    dueDate: family ? (family.dueDate ? formatDate(family.dueDate) : 'as soon as possible') : '—',
    session: session ?? '—',
    institute: institute ?? '—',
  };

  return template.replace(/\{([^{}]*)\}/g, (whole, token: string) => values[token] ?? whole);
}

/** "1 student", "12 students" — a reminder goes to one family per student. */
function countStudents(count: number): string {
  return `${count} ${count === 1 ? 'student' : 'students'}`;
}

/** Typed rupees as digits only — whole rupees, as the server takes them. */
function toRupeeDigits(value: string): string {
  return value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
}

/** The typed band as the API takes it: a blank end is no limit. */
function toBand(min: string, max: string): { minBalance?: number; maxBalance?: number } {
  return {
    ...(min !== '' && { minBalance: Number(min) }),
    ...(max !== '' && { maxBalance: Number(max) }),
  };
}

/** "₹5,000 or more", "₹1,000 to ₹5,000", "₹2,000 or less". */
function describeBand({ minBalance, maxBalance }: { minBalance?: number; maxBalance?: number }): string {
  const rupees = (amount: number) => formatCurrency(String(amount));
  if (minBalance !== undefined && maxBalance !== undefined) {
    return `${rupees(minBalance)} to ${rupees(maxBalance)}`;
  }
  if (minBalance !== undefined) return `${rupees(minBalance)} or more`;
  return `${rupees(maxBalance ?? 0)} or less`;
}

/** "overdue", "due in 7 days" — a due-date choice inside a sentence. */
function describeDue(choice: DueWhenChoice): string {
  return (DUE_WHEN_CHOICES.find((option) => option.key === choice)?.label ?? '').toLowerCase();
}

/** "Aarav, Diya and 12 more" — a list that never runs off the dialog. */
function NameList({ names }: { names: string[] }) {
  return (
    <>
      {names.slice(0, 5).join(', ')}
      {names.length > 5 && ` and ${names.length - 5} more`}
    </>
  );
}
